import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useCheckout } from '@/hooks/useCheckout'
import { usePayment } from '@/hooks/usePayment'
import { PaymentMethods } from '@/components/payment/PaymentMethods'
import { PixPayment } from '@/components/payment/PixPayment'
import { BoletoPayment } from '@/components/payment/BoletoPayment'
import { CreditCardForm } from '@/components/payment/CreditCardForm'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import { formatCurrency } from '@/lib/utils'
import { ArrowLeft, BadgeCheck, Loader2, Lock, ShieldCheck, ShoppingCart } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { fetchTrackingConfig, initPixels, trackEvent, trackUmami, trackUmamiToWebsite } from '@/lib/tracking'

const QUIZ_UMAMI_WEBSITE_ID = '1893b04d-8808-4edf-9932-f0b0706e58cb'
import type { CheckoutFormData, PaymentMethod } from '@/types/checkout'

function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

function normalizePhoneToE164(rawValue: string, defaultCountryDialCode: string = '55'): string {
  const raw = rawValue.trim()
  if (!raw) {
    return ''
  }

  const digits = raw.replace(/\D/g, '')
  if (!digits) {
    return ''
  }

  if (raw.startsWith('+')) {
    return `+${digits}`
  }

  if (raw.startsWith('00')) {
    return digits.startsWith('00') && digits.length > 2 ? `+${digits.slice(2)}` : `+${digits}`
  }

  if (digits.startsWith(defaultCountryDialCode) && digits.length > defaultCountryDialCode.length + 6) {
    return `+${digits}`
  }

  return `+${defaultCountryDialCode}${digits}`
}

export default function CheckoutPage() {
  const { templateSlug } = useParams<{ templateSlug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const orderParams = new URLSearchParams(location.search)
  const orderFromQuery = orderParams.get('order_number') || orderParams.get('order')
  const loadedOrderRef = useRef<string | null>(null)

  const [formData, setFormData] = useState<CheckoutFormData>({
    customer: {
      name: '',
      email: '',
      phone: '',
      document: '',
    },
    payment_method: 'pix' as PaymentMethod,
  })

  const {
    template,
    product,
    discount,
    customerPrefill,
    order,
    step,
    isLoading,
    error,
    processCheckout,
    loadOrderById,
  } = useCheckout({
    templateSlug: templateSlug!,
    queryString: location.search,
  })

  const {
    paymentMethod,
    paymentData,
    setPaymentMethod,
    createPixPayment,
    createBoletoPayment,
  } = usePayment({
    orderId: order?.id || '',
    onError: (error) => {
      toast({
        title: 'Erro no pagamento',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  useEffect(() => {
    if (!customerPrefill) {
      return
    }

    setFormData((previous) => ({
      ...previous,
      customer: {
        ...previous.customer,
        name: previous.customer.name || customerPrefill.name || '',
        email: previous.customer.email || customerPrefill.email || '',
        phone: previous.customer.phone || normalizePhoneToE164(customerPrefill.phone || '', '55'),
        document: previous.customer.document || customerPrefill.document || '',
      },
    }))
  }, [customerPrefill])

  const finalAmount = discount ? discount.discounted_price : product?.price ?? 0

  const pixelsInitRef = useRef(false)
  useEffect(() => {
    if (!product || pixelsInitRef.current) return
    pixelsInitRef.current = true
    fetchTrackingConfig().then((config) => {
      initPixels(config)
      trackEvent('ViewContent', {
        value: product.price,
        currency: 'BRL',
        content_name: product.name,
      })
      trackEvent('InitiateCheckout', {
        value: product.price,
        currency: 'BRL',
        content_name: product.name,
      })

      // Espelha o InitiateCheckout no website Umami do funil do quiz (cross-domain),
      // correlacionando pela query string lead_id/code vinda do quiz.
      if (templateSlug === 'quiz-sabotadores') {
        trackUmamiToWebsite(QUIZ_UMAMI_WEBSITE_ID, 'InitiateCheckout', {
          content_name: 'Quiz Sabotadores',
          lead_id: orderParams.get('lead_id') || undefined,
          code: orderParams.get('code') || undefined,
          value: product.price,
          currency: 'BRL',
        })
      }
    }).catch(() => {})
  }, [product?.price])

  // Cart abandonment tracking
  useEffect(() => {
    if (!product) return

    const handleBeforeUnload = () => {
      if (step === 'checkout' || (step === 'payment' && !order?.payment_status)) {
        trackUmami('Checkout Abandonado', {
          produto: product.name,
          valor: finalAmount,
          etapa: step === 'checkout' ? 'dados_pessoais' : 'pagamento',
          metodo_pagamento: paymentMethod || 'nenhum',
        })
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && (step === 'checkout' || step === 'payment')) {
        trackUmami('Checkout Saiu da Aba', {
          produto: product.name,
          etapa: step,
        })
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [product, step, order?.payment_status, paymentMethod, finalAmount])

  useEffect(() => {
    if (!orderFromQuery || order || loadedOrderRef.current === orderFromQuery) {
      return
    }

    loadedOrderRef.current = orderFromQuery

    loadOrderById(orderFromQuery).catch(() => {
      loadedOrderRef.current = null
      const params = new URLSearchParams(location.search)
      params.delete('order_number')
      params.delete('order')

      navigate(
        params.toString()
          ? `${location.pathname}?${params.toString()}`
          : location.pathname,
        { replace: true }
      )

      toast({
        title: 'Pedido não encontrado',
        description: 'Gere um novo pedido para continuar.',
        variant: 'destructive',
      })
    })
  }, [loadOrderById, location.pathname, location.search, navigate, order, orderFromQuery, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await processCheckout({
        ...formData,
        customer: {
          ...formData.customer,
          phone: normalizePhoneToE164(formData.customer.phone, '55'),
        },
      })

      const params = new URLSearchParams(location.search)
      params.set('order_number', response.order.order_number)
      params.delete('order')
      navigate(`${location.pathname}?${params.toString()}`, { replace: true })

      trackEvent('AddPaymentInfo', {
        value: finalAmount,
        currency: 'BRL',
        content_name: product?.name,
      })

      toast({
        title: 'Tudo certo!',
        description: 'Agora escolha a forma de pagamento',
      })
    } catch (error: any) {
      toast({
        title: 'Erro ao processar',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const handlePaymentMethodSelect = async (method: PaymentMethod) => {
    setPaymentMethod(method)

    const methodLabels: Record<string, string> = {
      pix: 'PIX',
      boleto: 'Boleto',
      credit_card: 'Cartao de Credito',
    }
    trackUmami('Selecionou Forma de Pagamento', {
      metodo: methodLabels[method] || method,
      produto: product?.name,
      valor: finalAmount,
    })

    if (!order) return

    try {
      if (method === 'pix') {
        await createPixPayment()
      } else if (method === 'boleto') {
        await createBoletoPayment()
      }
      // credit_card será processado pelo formulário
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Carregando checkout...</p>
        </div>
      </div>
    )
  }

  if (error || !template || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-center space-y-8 max-w-md">
          <div className="space-y-4">
            <h1 className="text-9xl font-bold text-foreground opacity-90">
              404
            </h1>
            <h2 className="text-2xl font-semibold text-foreground">
              Página não encontrada
            </h2>
            <p className="text-muted-foreground text-base">
              A página que você procura não existe ou foi removida.
            </p>
          </div>

          <div className="flex gap-3 justify-center pt-4">
            <Button
              onClick={() => window.location.href = 'https://www.slac.com.br'}
              variant="outline"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const brandLogo = template.layout.logo_url || '/assets/slac/slac-logo.svg'
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4 py-12">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8">
          <div className="flex items-center gap-3">
            <img
              src={brandLogo}
              alt="SLAC"
              className="h-8 w-auto"
              loading="eager"
            />
            <Separator orientation="vertical" className="h-6" />
            <div>
              <h1 className="text-lg font-semibold leading-tight">{template.name}</h1>
              <p className="text-xs text-muted-foreground">Checkout seguro e criptografado</p>
            </div>
          </div>
        </header>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Formulário - 2 colunas */}
          <div className="lg:col-span-2 space-y-6">
            {step === 'checkout' && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados pessoais</CardTitle>
                  <CardDescription>Preencha seus dados para continuar</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome completo</Label>
                      <Input
                        id="name"
                        value={formData.customer.name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customer: { ...formData.customer, name: e.target.value },
                          })
                        }
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.customer.email}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customer: { ...formData.customer, email: e.target.value },
                          })
                        }
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone</Label>
                        <PhoneInput
                          id="phone"
                          defaultCountry="BR"
                          international
                          countryCallingCodeEditable={false}
                          value={formData.customer.phone || undefined}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              customer: {
                                ...formData.customer,
                                phone: value || '',
                              },
                            })
                          }
                          autoComplete="tel"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="document">CPF</Label>
                        <Input
                          id="document"
                          placeholder="000.000.000-00"
                          value={formData.customer.document}
                          maxLength={14}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customer: { ...formData.customer, document: formatCpf(e.target.value) },
                            })
                          }
                          required
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" size="lg">
                      Continuar para pagamento
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 'payment' && order && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Forma de pagamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaymentMethods
                      availableMethods={template.payment.methods}
                      selectedMethod={paymentMethod}
                      onSelectMethod={handlePaymentMethodSelect}
                    />
                  </CardContent>
                </Card>

                {paymentMethod === 'pix' && paymentData && 'pix' in paymentData && (
                  <PixPayment pixData={paymentData} />
                )}

                {paymentMethod === 'boleto' && paymentData && 'boleto' in paymentData && (
                  <BoletoPayment boletoData={paymentData} />
                )}

                {paymentMethod === 'credit_card' && order && (
                  <CreditCardForm
                    orderId={order.id}
                    amount={finalAmount}
                    installmentsConfig={template.payment.credit_card!.installments}
                    onSuccess={(payment) => navigate(`/${payment.status === 'paid' ? 'success' : 'processing'}/${order.id}`)}
                    onError={(error) => {
                      toast({
                        title: 'Erro no pagamento',
                        description: error?.message || 'Não foi possível processar o cartão',
                        variant: 'destructive',
                      })
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Resumo - 1 coluna */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Resumo do pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {product.image_url && (
                  <div>
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full rounded-lg"
                    />
                  </div>
                )}

                <div>
                  <h3 className="font-semibold">{product.name}</h3>
                  <p className="text-sm text-muted-foreground">{product.description}</p>
                </div>

                {product.features && product.features.length > 0 && (
                  <div className="space-y-1">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  {product.original_price && product.original_price > product.price && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">De:</span>
                      <span className="text-muted-foreground line-through">
                        {formatCurrency(product.original_price)}
                      </span>
                    </div>
                  )}

                  {product.original_price && product.original_price > product.price && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Por:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-green-600">
                          {formatCurrency(product.price)}
                        </span>
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                          {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                        </span>
                      </div>
                    </div>
                  )}

                  {discount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Preço original:</span>
                      <span className="line-through">{formatCurrency(product.price)}</span>
                    </div>
                  )}

                  {discount && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Desconto ({discount.code}):</span>
                      <span>
                        -{formatCurrency(product.price - discount.discounted_price)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-primary">{formatCurrency(finalAmount)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Garantia */}
            {template.layout.sections?.guarantee?.enabled && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-8 w-8 shrink-0 text-emerald-600" />
                  <div>
                    <h4 className="font-semibold text-emerald-800 dark:text-emerald-300">
                      {template.layout.sections.guarantee.days} dias de garantia incondicional
                    </h4>
                    {template.layout.sections.guarantee.text && (
                      <p className="text-sm text-emerald-700 dark:text-emerald-400">
                        {template.layout.sections.guarantee.text}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="mt-8 rounded-xl p-4">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Transação segura</h3>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <p className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Criptografia TLS e proteção antifraude
                </p>
                <p className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-blue-600" />
                  Ambiente protegido com padrões de checkout
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Gateway de pagamento</h3>
              <div className="flex flex-wrap items-center gap-3">
                <img src="/assets/images/partners/logo-pagarme.png" alt="Pagar.me" className="h-10 min-h-[12px]" />
                <img src="/assets/images/partners/stone-logo.png" alt="Stone" className="h-4 min-h-[12px]" />
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <BadgeCheck className="h-4 w-4 text-emerald-600" />
            Pagamento processado em ambiente seguro. Dados protegidos e criptografados durante toda a transação.
          </p>
        </footer>
      </div>
    </div>
  )
}
